import config from "config";

import { FC, Fragment, useEffect, useState } from "react";
import dayjs from "dayjs";
// i18next
import { useTranslation } from "react-i18next";
// Hooks
import useFormattedDate from "app/hooks/useFormattedDate";
import useToggle from "app/hooks/useToggle";
import useMenu from "app/hooks/useMenu";
// Redux
import { useAppDispatch, useAppSelector } from "app/hooks/useStore";
// Models
import { RootState } from "app/store";
import IBillingInvoice, { IInvoiceLine } from "app/models/BillingInvoice";
// Async
import { exportInvoicesToPDF } from "app/store/BillingInvoices/BillingInvoices.async";
// Actions
import { BillingInvoicesActions } from "app/store/BillingInvoices/BillingInvoices.slice";
// Selectors
import { selectInvoicesExpandedAll, selectIsInvoiceIdInBatch } from "app/store/BillingInvoices/BillingInvoices.selectors";
import { selectCompanyNameByAccountId } from "app/store/Clients/Clients.selectors";
// Components
import { IconButton } from "app/components/Mui";
// MUI
import { Box, Checkbox, Collapse, Divider, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
// Icons
import { MoreVert as MoreVertIcon, KeyboardArrowUp as KeyboardArrowUpIcon, KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
//
import InvoiceDetailsDrawer from "./InvoiceDetailsDrawer";
import ACHPaymentFormDialog from "./ACHPaymentFormDialog";
import CreditCardPaymentFormDialog from "./CreditCardPaymentFormDialog";
// Context
import { useInvoiceColumnsContext } from "./InvoiceColumns.context";
// Mixpanel
import MixpanelActions from "app/features/mixpanel/MixpanelActions";
// Utilities
import { toNumberWithCommas } from "app/utilities/Utilities";

const invoiceDetailsTableLeftMargin = 18;
const invoiceDetailsTableWidth = '40%';

type Props = {
  invoice: IBillingInvoice;
}

const InvoicesListItem:FC<Props> = ({ invoice }) => {
  const { t } = useTranslation();

  const { isVisible, visibleColumnsCount } = useInvoiceColumnsContext();

  const dispatch = useAppDispatch();

  const [ open, setOpen ] = useState<boolean>(false);
  const toggle = () => setOpen(!open);

  const isInvoiceIdInBatch = useAppSelector((state:RootState) => selectIsInvoiceIdInBatch(state, {
    invoiceId: invoice.id
  }));
  const companyName = useAppSelector((state:RootState) => selectCompanyNameByAccountId(state, {
    accountId: invoice.insuranceCase?.clientAccountId
  }));
  const invoicesExpandedAll = useAppSelector(selectInvoicesExpandedAll);

  const dueDate = useFormattedDate(invoice.dueDate);
  const periodStart = useFormattedDate(invoice.periodStart);
  const periodEnd = useFormattedDate(dayjs(invoice.periodEnd).subtract(1, 'day').toISOString());

  const dueDateIsInPast = invoice.status === 'sent' && invoice.dueDate
    ? dayjs(invoice.dueDate).isBefore(dayjs(), 'day')
    : false
  ;

  const statusLabels:Record<string, string> = {
    sent: t('labels.outstanding'),
    paid: t('labels.paid')
  }

  const { open:openDetailsDrawer, toggle:toggleDetailsDrawer } = useToggle();
  const { Menu, MenuItem, openMenu } = useMenu();

  const { open:openACHPaymentFormDialog, toggle:toggleACHPaymentFormDialog } = useToggle();
  const { open:openCreditCardPaymentFormDialog, toggle:toggleCreditCardPaymentFormDialog } = useToggle();

  const handleExportInvoiceToPDF = async () => {
    try {
      await dispatch(exportInvoicesToPDF([invoice.id])).unwrap();
      dispatch(MixpanelActions.handleBillingInvoicesPDFExportClick({ invoiceId: invoice.id }));
    } catch(error){}
  }

  const handleChange = (e:any) => {
    e.stopPropagation();
    dispatch(BillingInvoicesActions.toggleBatchInvoice(invoice));
  }

  useEffect(() => {
    setOpen(invoicesExpandedAll);
  }, [invoicesExpandedAll]);

  const billingPeriod = periodStart && periodEnd
    ? `${periodStart} - ${periodEnd}`
    : null;

  return (
    <Fragment>
      <TableRow
        sx={{
          '&:last-child td, &:last-child th': { border: 0 },
          bgcolor: isInvoiceIdInBatch ? theme => theme.palette.primary.light : 'inherit'
        }}
        onClick={toggle}
      >
        <TableCell padding="checkbox">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Checkbox
              checked={isInvoiceIdInBatch}
              onClick={handleChange}
            />
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </Box>
        </TableCell>
        {isVisible('invoiceNumber') && (
          <TableCell component="th" scope="row" sx={{ py: 1, px: 2 }}>
            {invoice.invoiceNumber}
          </TableCell>
        )}
        {isVisible('billingPeriod') && (
          <TableCell sx={{ py: 1, px: 2 }}>{billingPeriod}</TableCell>
        )}
        {isVisible('client') && (
          <TableCell sx={{ py: 1, px: 2 }}>{companyName}</TableCell>
        )}
        {isVisible('description') && (
          <TableCell sx={{ py: 1, px: 2, maxWidth: 400 }}>{invoice.description}</TableCell>
        )}
        {isVisible('case') && (
          <TableCell sx={{ py: 1, px: 2 }}>{invoice.insuranceCase?.name}</TableCell>
        )}
        {isVisible('status') && (
          <TableCell sx={{ py: 1, px: 2 }}>
            {statusLabels[invoice.status]}
            {dueDateIsInPast ? (
              <Box component="span" color="red">{` (${t('labels.overdue')})`}</Box>
            ) : null}
          </TableCell>
        )}
        {isVisible('dueDate') && (
          <TableCell sx={{ py: 1, px: 2 }}>{dueDate}</TableCell>
        )}
        {isVisible('amount') && (
          <TableCell sx={{ py: 1, px: 2 }}>${toNumberWithCommas(invoice.amount)}</TableCell>
        )}
        <TableCell align="right" sx={{ py: 1, px: 2 }}>
          <IconButton
            name={`invoice-actions-${invoice.id}`}
            onClick={openMenu}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu>
            <MenuItem name="invoice-details" onClick={toggleDetailsDrawer}>{t('labels.details')}</MenuItem>
            <Divider />
            {invoice.status !== 'paid' ? (
              <Fragment>
                {config.domain === '.com' ? (
                  <MenuItem
                    name={`ACH payment ${invoice.id}`}
                    onClick={toggleACHPaymentFormDialog}
                  >{t('pages.billingAndSubscriptions.achPaymentButton')}</MenuItem>
                ) : null}
                <MenuItem
                  name={`Pay with credit card ${invoice.id}`}
                  onClick={toggleCreditCardPaymentFormDialog}
                >{t('pages.billingAndSubscriptions.creditCardPaymentButton')}</MenuItem>
              </Fragment>
            ) : null}
            <MenuItem
              name={`PDF Export ${invoice.id}`}
              onClick={handleExportInvoiceToPDF}
            >{t('pages.billingAndSubscriptions.exportToPdfButton')}</MenuItem>
          </Menu>
        </TableCell>
      </TableRow>
      <TableRow sx={{
        bgcolor: isInvoiceIdInBatch ? theme => theme.palette.primary.light : 'rgba(0,0,0,0.05)'
      }}>
        <TableCell sx={{ py: 0 }} colSpan={visibleColumnsCount + 2}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ mb: 2, ml: invoiceDetailsTableLeftMargin, width: invoiceDetailsTableWidth }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontSize: 11, borderBottom: 'none' } }}>
                    <TableCell sx={{ borderBottom: 'none' }}></TableCell>
                    <TableCell align="right">{t('labels.qty')}</TableCell>
                    <TableCell align="right">{t('labels.rate')}</TableCell>
                    <TableCell align="right">{t('labels.amount')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.lines.map((line:IInvoiceLine, index:number) => (
                    <TableRow
                      key={`billing invoice item ${invoice.id} line ${index}`}
                      sx={{ '& th, td': { fontSize: 12, borderBottom: 'none' } }}
                    >
                      <TableCell component="th" scope="row">
                        {line.name}
                      </TableCell>
                      <TableCell align="right">{line.quantity}</TableCell>
                      <TableCell align="right">{line.price < 0 ? `-$${Math.abs(line.price)}` : `$${line.price}`}</TableCell>
                      <TableCell align="right">{line.amount < 0 ? `-$${toNumberWithCommas(Math.abs(line.amount))}` : `$${toNumberWithCommas(line.amount)}`}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <InvoiceDetailsDrawer
        open={openDetailsDrawer}
        onClose={toggleDetailsDrawer}
        invoice={invoice}
      />
      {openACHPaymentFormDialog ? (
        <ACHPaymentFormDialog
          open={openACHPaymentFormDialog}
          onClose={toggleACHPaymentFormDialog}
          invoice={invoice}
        />
      ) : null}
      {openCreditCardPaymentFormDialog ? (
        <CreditCardPaymentFormDialog
          open={openCreditCardPaymentFormDialog}
          onClose={toggleCreditCardPaymentFormDialog}
          invoice={invoice}
        />
      ) : null}
    </Fragment>
  )
}

export default InvoicesListItem;
